import { getCargoPortLaytimeStats } from '@shared/utils/getCargoPortLaytimeStats';
import { capitalize } from '@shared/utils/string';
import { LaytimeTerms } from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { getDemurrageTermLabel } from '@shared/utils/demurrageTerm';
import {
	dndRateUnitLabel,
	hoursToInterval,
	intervalTimeUnit,
} from '@shared/utils/laytime';
import { TemplateItemChildren } from '@shared/hireInvoice/HireInvoiceItem';
import { CargoProps } from '@api/models/cargo';
import { CargoPortWithTurnDays } from '@api/features/laytime/getCargosAndLaytimes';

export const getLaytimeInvoiceLineDescriptions = (
	cargo: CargoProps & {
		CargoPorts: Array<CargoPortWithTurnDays>;
	},
): TemplateItemChildren => {
	const { laytimeTerms } = cargo;

	return cargo.CargoPorts.map((cargoPort) => {
		const {
			action,
			timeGainedOrLost,
			isDem,
			totalDemurrage,
			totalDespatch,
		} = getCargoPortLaytimeStats(cargoPort, cargo);

		const type = isDem ? getDemurrageTermLabel(cargo.demurrageTerm) : 'Despatch';
		const time = isDem ? 'lost' : 'gained';
		const rate = isDem ?
			formatCurrency(cargoPort.demurrage ?? 0, cargo.currency) :
			formatCurrency(cargoPort.despatch ?? 0, cargo.currency);
		const rateUnit = dndRateUnitLabel(cargo.demurrageDespatchInterval);
		const timeAllowedInterval = laytimeTerms === LaytimeTerms.NON_REVERSIBLE ?
			cargoPort.laytimeInterval :
			cargo.timeAllowedInterval;
		const timeValue = hoursToInterval(
			Math.abs(timeGainedOrLost) / 60,
			timeAllowedInterval,
		);
		const timeUnit = intervalTimeUnit(timeAllowedInterval, timeValue);

		switch (laytimeTerms) {
			case LaytimeTerms.REVERSIBLE:
			case LaytimeTerms.AVERAGED:
				return {
					section: `${cargoPort.port.name} - ${capitalize(action)}`,
					columns: [],
				};

			case LaytimeTerms.NON_REVERSIBLE:
				return {
					section: `${cargoPort.port.name} - ${capitalize(action)}`,
					columns: [
						`${timeValue} ${timeUnit} ${time}`,
						`${type} @ ${rate} ${rateUnit}`,
					],
					total: isDem ? totalDemurrage : totalDespatch,
				};

			default:
				return {
					section: '',
					columns: ['', ''],
				};
		}
	});
};
